import { useEffect, useState, useCallback } from 'react';
import { BiCheckCircle } from 'react-icons/bi';
import { AiOutlineClose, AiOutlineLoading, AiOutlineMail, AiOutlinePrinter } from 'react-icons/ai';
import { MdContentCopy, MdCheckCircleOutline, MdQrCode, MdMoneyOff, MdRefresh } from 'react-icons/md';
import ModalPortal from "./lib/modalPortal";
import { formatDate } from '../utils/DateUtils';
import { formatCurrency } from '../utils/CurrencyUtils';
import { createItemListFromString, formatString, getStudentName } from '../utils/StringUtils';
import qs from 'qs';
import { toast } from 'react-toastify';
import ClientService from '../rest/clientService';
import NotificationsService from '../rest/notificationService';
import PaymentService from '../rest/paymentsService';
import { useAuth } from '../auth/AuthenticationWrapper';

const InfoCard = ({ label, value, colorClass = "text-gray-800" }) => (
    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 shadow-sm flex flex-col justify-between h-full">
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</span>
        <span className={`font-semibold text-sm ${colorClass} break-words`}>{value}</span>
    </div>
);

export default function OrderInfo({ order, showOrderInfoModal, handleCancelClick, customer }) {
    const [clientInfo, setClientInfo] = useState();
    const [send, setSend] = useState(null);
    const [pixQr, setPixQr] = useState(null);
    const [loadingQr, setLoadingQr] = useState(false);
    const [copiedQr, setCopiedQr] = useState(false);
    const { hasPermission } = useAuth();
    const canSendNotifications = hasPermission("customers", "send_notifications");
    const canReprintReceipt = hasPermission("customers", "reprint_receipts");
    const canRefund = hasPermission("orders", "cancel_order"); // canceladores também podem reembolsar
    const role = sessionStorage.getItem('role');
    const clientCode = sessionStorage.getItem('clientCode');

    // ── Refund state ──────────────────────────────────────────────────────────
    const [refundData, setRefundData]               = useState(undefined); // undefined=não carregado, null=inexistente
    const [refundLoading, setRefundLoading]         = useState(false);
    const [showRefundConfirm, setShowRefundConfirm] = useState(false);
    const [refundRequesting, setRefundRequesting]   = useState(false);

    // ── Refund helpers ────────────────────────────────────────────────────────

    /**
     * Condição para exibir o bloco de reembolso:
     * - Pedido CANCELED com transação via Asaas (transactionSource=ASAAS) e
     *   pagamento confirmado (asaasStatus=CONFIRMED) no gateway.
     * - Recargas online (Credit Recharge Asaas) são tratadas em v2
     *   (complexidade de crédito parcialmente consumido).
     */
    const mainTx = order?.transactions?.[0];
    const isOnlineOrderRefundEligible =
        order?.transaction_type !== 'Credit Recharge Admin'
        && order?.transaction_type !== 'Credit Recharge Asaas'
        && order?.transaction_type !== 'Credit Expense'
        && order?.status === 'CANCELED'
        && mainTx?.transactionSource === 'ASAAS'
        && mainTx?.asaasStatus === 'CONFIRMED';

    const showRefundBlock = isOnlineOrderRefundEligible;

    const refundEntityId = String(order?.order_id ?? '');

    const isCardPayment = mainTx?.paymentMethod &&
        (mainTx.paymentMethod.startsWith('CREDITO') || mainTx.paymentMethod.startsWith('DEBITO'));

    const loadRefundStatus = useCallback(async () => {
        if (!showRefundBlock || !clientCode || !order?.order_id) return;
        setRefundLoading(true);
        try {
            const data = await PaymentService.getRefundStatus('ORDER', refundEntityId, clientCode);
            setRefundData(data); // null se 404
        } catch {
            setRefundData(null);
        } finally {
            setRefundLoading(false);
        }
    }, [showRefundBlock, clientCode, refundEntityId]);

    const handleRequestRefund = async () => {
        setRefundRequesting(true);
        try {
            const result = await PaymentService.requestRefund({
                entityType: 'ORDER',
                entityId: refundEntityId,
                entityNumericId: order.order_id,
                clientCode,
            });
            setRefundData(result);
            setShowRefundConfirm(false);
            if (result.refundStatus === 'PROCESSED') {
                toast.success('Reembolso processado com sucesso!');
            } else if (result.refundStatus === 'FAILED') {
                toast.warning('Solicitação registrada, mas o gateway retornou falha. Tente novamente.');
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Erro ao solicitar reembolso.';
            toast.error(msg);
        } finally {
            setRefundRequesting(false);
        }
    };

    const handleRetryRefund = async () => {
        if (!refundData?.id) return;
        setRefundRequesting(true);
        try {
            const result = await PaymentService.retryRefund(refundData.id, clientCode);
            setRefundData(result);
            if (result.refundStatus === 'PROCESSED') {
                toast.success('Reembolso processado com sucesso!');
            } else {
                toast.warning('Nova tentativa falhou. Tente novamente ou contate o suporte.');
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Erro ao retentar reembolso.';
            toast.error(msg);
        } finally {
            setRefundRequesting(false);
        }
    };

    const translatePaymentMethod = (method) => {
        if (method === 'CREDIT_CARD') return 'Cartão de Crédito';
        return method || '';
    };

    const translateAsaasStatus = (status) => {
        switch (status) {
            case 'CONFIRMED': return 'Confirmado';
            case 'PENDING':   return 'Pendente';
            case 'FAILED':    return 'Falhou';
            default:          return status || '';
        }
    };

    const asaasStatusColor = (status) => {
        if (status === 'CONFIRMED') return 'bg-green-100 text-green-700';
        if (status === 'FAILED')    return 'bg-red-100 text-red-700';
        return 'bg-yellow-100 text-yellow-700';
    };

    const displayStatus = (status, transaction_type) => {
        if (transaction_type === "Credit Recharge Admin") return "Recarga Manual";
        if (transaction_type === "Credit Recharge Asaas") return "Recarga de Saldo";
        if (transaction_type === "Credit Expense") return "Lançamento de Correção";
        switch (status) {
            case "COMPLETED": return "Pago - Aguardando Finalização";
            case "PROCESSINGPAYMENT": return "Processando Pagamento";
            case "FINISHED": return "Finalizado";
            case "CANCELED": return "Cancelado";
            default: return status;
        }
    };

    const isSendEnabled = async () => {
        try {
            const preferences = await ClientService.getClientAdminPreferences();
            setSend(preferences?.enable_send_notifications === true);
        } catch (e) { console.error(e); }
    }

    useEffect(() => {
        const info = JSON.parse(sessionStorage.getItem('clientInfo'));
        setClientInfo(info);
        isSendEnabled();
        setPixQr(null);
        // Reset refund state on order change
        setRefundData(undefined);
        setShowRefundConfirm(false);
    }, [order]);

    useEffect(() => {
        if (showRefundBlock) loadRefundStatus();
    }, [showRefundBlock, loadRefundStatus]);

    const handleLoadPixQr = async () => {
        setLoadingQr(true);
        try {
            const data = await PaymentService.getPixQrCode(order.asaasRechargeId);
            if (data?.qrCodeEncodedImage || data?.qrCodePayload) {
                setPixQr({ qrCodeEncodedImage: data.qrCodeEncodedImage, qrCodePayload: data.qrCodePayload });
            }
        } catch (e) {
            console.error("Erro ao buscar QR code:", e);
        } finally {
            setLoadingQr(false);
        }
    };

    const handleCopyQr = () => {
        if (!pixQr?.qrCodePayload) return;
        navigator.clipboard.writeText(pixQr.qrCodePayload);
        setCopiedQr(true);
        setTimeout(() => setCopiedQr(false), 2000);
    };

    if (!showOrderInfoModal || !order) return null;

    const isRecharge = order.transaction_type === "Credit Recharge Admin" || order.transaction_type === "Credit Recharge Asaas";
    const isExpense = order.transaction_type === "Credit Expense";
    let student = order.client_id ? getStudentName(order.client_id) : getStudentName(order.customer_id);
    let selectedProducts = !isRecharge && !isExpense ? createItemListFromString(order.items_list) : [];

    const calculateTotalReceived = () => {
        if (isRecharge) return parseFloat(order.value || 0);
        return (order.transactions || []).reduce((acc, transaction) => acc + (parseFloat(transaction.value) || 0), 0);
    };

    const calculateChange = () => {
        const totalReceived = calculateTotalReceived();
        const totalOrder = parseFloat(isRecharge ? order.value : order.total) || 0;
        return totalReceived - totalOrder;
    };

    const handleReceipt = async () => {
        const queryString = qs.stringify({
            customer: isRecharge ? JSON.stringify(customer.name) : JSON.stringify(student),
            orderId: isRecharge ? order.transactionId : order.order_id,
            date: new Date(isRecharge ? order.transactionDate : order.purchase_date),
            selectedProducts: JSON.stringify(selectedProducts),
            currentCredit: JSON.stringify(
                customer.isPayingStudent === true ?
                    isRecharge ? order.credit_afterwards : (order.transactions?.[0]?.credit_afterwards || customer.credit)
                    : customer.credit
            ),
            grade: JSON.stringify(customer.grade),
            transactions: isRecharge ? [order] : order.transactions,
            totalAmount: isRecharge ? order.value : order.total,
            change: calculateChange(),
            operator: isRecharge ? order.paymentMethod : order.cashier_id,
            clientInfo: JSON.stringify(clientInfo),
            print: false,
        });

        const features = 'toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=no,resizable=no,copyhistory=no,left=100,top=100,width=340,height=600';
        const url = isRecharge ? `/comprovanteRecarga?${queryString}` : `/comprovante?${queryString}`;
        window.open(url, '_blank', features);
    };

    const handleSendEmail = async () => {
        try {
            const studentData = {
                email: customer.email,
                name: customer.name,
                grade: customer.grade,
                id_student: customer.id_student,
                login: customer.login,
                phone_number: customer.phone_number,
                emergency_value: customer.emergency_value,
                spend_limit: customer.spend_limit,
                client_code: customer.client_code,
                username: customer.username,
                print: false,
            };

            if (isRecharge) {
                const balanceAfter = order.credit_afterwards;
                const balanceBefore = balanceAfter - Number(order.value || 0);
                await NotificationsService.sendEmailNotificationForCreditRecharge(
                    clientInfo, studentData, [order], balanceBefore, balanceAfter, order.paymentMethod
                );
            } else {
                await NotificationsService.sendEmailNotificationForOrder(
                    clientInfo, studentData, order, order.cashier_id, calculateChange(), selectedProducts
                );
            }
            alert("Email enviado com sucesso!");
        } catch (e) {
            console.error(e);
            alert("Erro ao enviar email.");
        }
    };

    const renderPaymentMethods = () => {
        if (isRecharge) {
            return (
                <div className="flex justify-between items-center text-sm p-2 bg-white rounded border border-slate-100">
                    <span className="text-gray-600">{translatePaymentMethod(order.paymentMethod)}</span>
                    <span className="font-bold text-gray-800">{formatCurrency(order.value)}</span>
                </div>
            );
        }
        return (order.transactions || []).map((transaction, index) => (
            <div key={index} className="flex justify-between items-center text-sm p-2 bg-white rounded border border-slate-100 mb-1 last:mb-0">
                <span className="text-gray-600">{transaction.paymentMethod}</span>
                <span className="font-bold text-gray-800">{formatCurrency(transaction.value)}</span>
            </div>
        ));
    };

    const getTitle = () => {
        if (isRecharge) return "Detalhes da Recarga";
        if (isExpense) return "Detalhes da Correção";
        return "Detalhes do Pedido";
    };

    const getStatusColor = () => {
        if (order.status === 'CANCELED') return 'bg-red-100 text-red-700';
        if (order.status === 'FINISHED' || order.status === 'COMPLETED') return 'bg-green-100 text-green-700';
        return 'bg-blue-100 text-blue-700';
    };

    return (
        <ModalPortal>
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in duration-200 overflow-hidden">

                    {/* HEADER */}
                    <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-slate-50/50">
                        <div className="flex items-center gap-3">
                            <h2 className="text-xl font-bold text-gray-800">{getTitle()}</h2>
                            {!isExpense && !isRecharge && (
                                <span className={`text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wider ${getStatusColor()}`}>
                                    {displayStatus(order.status, order.transaction_type)}
                                </span>
                            )}
                            {order.transaction_type === "Credit Recharge Asaas" && order.asaasStatus && (
                                <span className={`text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wider ${asaasStatusColor(order.asaasStatus)}`}>
                                    {translateAsaasStatus(order.asaasStatus)}
                                </span>
                            )}
                        </div>
                        <button onClick={handleCancelClick} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-slate-200 transition-colors">
                            <AiOutlineClose size={20} />
                        </button>
                    </div>

                    {/* CONTENT */}
                    <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-6">

                        {/* ID e Vendedor */}
                        <div className="flex justify-between items-center bg-blue-50 p-3 rounded-lg border border-blue-100">
                            <div className="flex flex-col">
                                <span className="text-xs text-blue-500 font-bold uppercase">ID da Transação</span>
                                <span className="font-mono font-bold text-blue-800 text-lg">
                                    {isRecharge || isExpense ? order.transactionId : order.order_id}
                                </span>
                            </div>
                            {!isRecharge && !isExpense && (
                                <div className="text-right">
                                    <span className="text-xs text-blue-500 font-bold uppercase">Vendedor</span>
                                    <span className="block font-semibold text-blue-700">{order.cashier_id}</span>
                                </div>
                            )}
                        </div>

                        {/* Grid de Informações Principais */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            <InfoCard
                                label="Cliente"
                                value={getStudentName(order.client_id) || getStudentName(order.customer_id) || customer?.name || 'Não Registrado'}
                            />
                            <InfoCard
                                label="Data"
                                value={formatDate(isRecharge || isExpense ? order.transactionDate : order.purchase_date)}
                            />
                            <InfoCard
                                label="Total"
                                value={formatCurrency(isRecharge || isExpense ? order.value : order.total)}
                                colorClass="text-green-600 font-bold text-lg"
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Pagamento e Observações */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {!isExpense && (
                                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                                        <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide border-b border-slate-200 pb-1">Pagamento</h3>
                                        <div className="space-y-2">
                                            {renderPaymentMethods()}
                                        </div>
                                    </div>
                                )}

                                {(order.observations || isExpense) && (
                                    <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-100">
                                        <h3 className="text-sm font-bold text-yellow-700 mb-2 uppercase tracking-wide">Observações</h3>
                                        <p className="text-sm text-yellow-900 italic">
                                            {order.observations || "Sem observações registradas."}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* PIX QR Code (Asaas recharge, pending PIX only) */}
                            {order.transaction_type === "Credit Recharge Asaas"
                                && order.asaasStatus === "PENDING"
                                && order.paymentMethod === "PIX"
                                && order.asaasRechargeId && (
                                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                                    <h3 className="text-sm font-bold text-blue-700 mb-3 uppercase tracking-wide flex items-center gap-2">
                                        <MdQrCode size={16} /> QR Code PIX
                                    </h3>
                                    {!pixQr ? (
                                        <button
                                            onClick={handleLoadPixQr}
                                            disabled={loadingQr}
                                            className="flex items-center gap-2 px-4 py-2 bg-custom_blue text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                                        >
                                            {loadingQr
                                                ? <><AiOutlineLoading className="animate-spin" /> Carregando...</>
                                                : <><MdQrCode size={16} /> Exibir QR Code</>}
                                        </button>
                                    ) : (
                                        <div className="space-y-3">
                                            {pixQr.qrCodeEncodedImage && (
                                                <img
                                                    src={`data:image/png;base64,${pixQr.qrCodeEncodedImage}`}
                                                    alt="QR Code PIX"
                                                    className="w-44 h-44 border border-slate-200 rounded-lg bg-white"
                                                />
                                            )}
                                            {pixQr.qrCodePayload && (
                                                <div>
                                                    <p className="text-xs text-blue-600 mb-1">Pix Copia e Cola:</p>
                                                    <div className="flex items-center gap-2 bg-white border border-blue-200 rounded-lg px-3 py-2">
                                                        <span className="flex-1 text-xs text-gray-700 truncate font-mono">{pixQr.qrCodePayload}</span>
                                                        <button onClick={handleCopyQr} className="shrink-0 text-custom_blue hover:text-blue-700 transition-colors">
                                                            {copiedQr
                                                                ? <MdCheckCircleOutline size={18} className="text-green-500" />
                                                                : <MdContentCopy size={18} />}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Lista de Itens (Apenas Vendas) */}
                        {!isRecharge && !isExpense && (
                            <div>
                                <h3 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide border-b border-slate-200 pb-1">
                                    Itens do Pedido
                                </h3>
                                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm text-gray-700 leading-relaxed font-mono">
                                    {order.items_list ? formatString(order.items_list) : 'Nenhum item registrado.'}
                                </div>
                            </div>
                        )}

                        {/* Bloco de Reembolso — apenas para pedidos cancelados pagos online */}
                        {showRefundBlock && canRefund && role !== 'PARENT' && (
                            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                                <h3 className="text-sm font-bold text-orange-700 mb-3 uppercase tracking-wide flex items-center gap-2 border-b border-orange-200 pb-2">
                                    <MdMoneyOff size={16} /> Reembolso Online
                                </h3>

                                {/* Carregando */}
                                {refundLoading && (
                                    <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                                        <AiOutlineLoading className="animate-spin text-orange-500" />
                                        Verificando status do reembolso...
                                    </div>
                                )}

                                {/* Sem reembolso solicitado */}
                                {!refundLoading && refundData === null && (
                                    <div>
                                        <p className="text-sm text-gray-600 mb-3">
                                            Nenhum reembolso solicitado para este pedido.
                                        </p>
                                        {!showRefundConfirm ? (
                                            <button
                                                onClick={() => setShowRefundConfirm(true)}
                                                className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-lg transition-colors"
                                            >
                                                <MdMoneyOff size={16} /> Solicitar Reembolso
                                            </button>
                                        ) : (
                                            <div className="p-3 bg-orange-100 border border-orange-300 rounded-lg space-y-3">
                                                <p className="text-sm font-semibold text-orange-800">
                                                    Confirmar reembolso de {formatCurrency(order.total)}?
                                                </p>
                                                {isCardPayment && (
                                                    <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded p-2">
                                                        ⚠️ Pagamento via cartão — o estorno pode levar até 30 dias para aparecer na fatura do cliente.
                                                    </p>
                                                )}
                                                {!isCardPayment && (
                                                    <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded p-2">
                                                        O valor será devolvido ao PIX do pagador em instantes.
                                                    </p>
                                                )}
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={handleRequestRefund}
                                                        disabled={refundRequesting}
                                                        className="flex-1 flex items-center justify-center gap-1 bg-orange-600 hover:bg-orange-700 text-white py-2 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                                                    >
                                                        {refundRequesting
                                                            ? <><AiOutlineLoading className="animate-spin" /> Processando...</>
                                                            : 'Confirmar Reembolso'}
                                                    </button>
                                                    <button
                                                        onClick={() => setShowRefundConfirm(false)}
                                                        disabled={refundRequesting}
                                                        className="flex-1 bg-white border border-orange-300 text-orange-700 py-2 text-sm font-medium rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-50"
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Reembolso em processamento */}
                                {!refundLoading && refundData?.refundStatus === 'PENDING' && (
                                    <div className="flex items-center gap-2">
                                        <AiOutlineLoading className="animate-spin text-orange-500" />
                                        <span className="text-sm text-orange-700 font-medium">Reembolso em processamento...</span>
                                    </div>
                                )}

                                {/* Reembolso processado */}
                                {!refundLoading && refundData?.refundStatus === 'PROCESSED' && (
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-bold px-2 py-1 rounded-full bg-green-100 text-green-700 border border-green-200 uppercase">
                                                ✓ Reembolso Processado
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            Processado em {refundData.processedAt
                                                ? new Date(refundData.processedAt).toLocaleString('pt-BR')
                                                : '—'}
                                            {refundData.requestedBy && ` · por ${refundData.requestedBy}`}
                                        </p>
                                    </div>
                                )}

                                {/* Reembolso falhou — pode retentar */}
                                {!refundLoading && (refundData?.refundStatus === 'FAILED' || refundData?.refundStatus === 'FAILED_PERMANENT') && (
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-xs font-bold px-2 py-1 rounded-full border uppercase ${
                                                refundData.refundStatus === 'FAILED_PERMANENT'
                                                    ? 'bg-red-100 text-red-700 border-red-200'
                                                    : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                            }`}>
                                                {refundData.refundStatus === 'FAILED_PERMANENT'
                                                    ? '✗ Falha Permanente'
                                                    : '⚠ Falha no Reembolso'}
                                            </span>
                                        </div>
                                        {refundData.failReason && (
                                            <p className="text-xs text-gray-500 italic">{refundData.failReason}</p>
                                        )}
                                        {refundData.refundStatus === 'FAILED_PERMANENT' ? (
                                            <p className="text-xs text-red-600">
                                                Número máximo de tentativas atingido. Contate o suporte Asaas.
                                            </p>
                                        ) : refundData.canRetry ? (
                                            <button
                                                onClick={handleRetryRefund}
                                                disabled={refundRequesting}
                                                className="flex items-center gap-2 px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
                                            >
                                                {refundRequesting
                                                    ? <><AiOutlineLoading className="animate-spin" /> Tentando...</>
                                                    : <><MdRefresh size={14} /> Tentar Novamente ({refundData.retryCount}/{5})</>}
                                            </button>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* FOOTER ACTIONS */}
                    <div className="p-4 border-t border-slate-100 bg-slate-50 flex flex-col sm:flex-row justify-between items-center gap-4">

                        {/* Botões de Ação (Esquerda) - Apenas Admin/Caixa */}
                        <div className="flex gap-2 w-full sm:w-auto">
                            {role !== 'PARENT' && !isExpense && (
                                <>
                                    {send && (
                                        <button
                                            onClick={handleSendEmail}
                                            disabled={!canSendNotifications}
                                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 text-sm font-medium"
                                            title="Enviar por Email"
                                        >
                                            <AiOutlineMail size={18} /> <span className="hidden sm:inline">Enviar Email</span>
                                        </button>
                                    )}
                                    <button
                                        onClick={handleReceipt}
                                        disabled={!canReprintReceipt}
                                        className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 text-sm font-medium"
                                        title="Imprimir Comprovante"
                                    >
                                        <AiOutlinePrinter size={18} /> <span className="hidden sm:inline">Imprimir</span>
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Botão Fechar (Direita) */}
                        <button
                            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-custom_green hover:bg-green-700 text-white font-bold py-2.5 px-8 rounded-xl shadow-md transition-all active:scale-95"
                            onClick={handleCancelClick}
                        >
                            <BiCheckCircle className="text-xl" /> OK
                        </button>
                    </div>

                </div>
            </div>
        </ModalPortal>
    );
}